// MARK://dhk8/pos/pages/CashoutPage
// Caja — Lane #162 redesign (touch-first, 2-col bills/coins grid, presets,
// movimientos intra-turno, resumen del día, imprimir comprobante).
//
// Backed by dhk8-pos-cashcuts (immutable cuts; varianceCents calc'd server-side).
// Kinds:
//   OPEN          → declaración de fondo inicial al abrir caja
//   CASH_DROP     → retiro mid-shift al safe (no cierra turno)
//   CASH_PICKUP   → ingreso mid-shift (cambio agregado) (no cierra turno)
//   X_REPORT      → snapshot del turno (no cierra)
//   Z_REPORT      → cierre del turno (resumen del día + cierra)
//   CLOSE         → legacy alias de Z_REPORT (igual cierra)
//
// Las acciones físicas (denominaciones, conteo) usan steppers grandes [-] [N] [+]
// (44px) en grid 2-col bills | coins, optimizado para tablet táctil del POS.

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Minus, Plus, Printer, Wallet, ArrowDownToLine, ArrowUpFromLine, ClipboardCheck, X, Ban, ClipboardList } from 'lucide-react'

import { useCashcutStore, type CashCutDto, type CashCutKind } from '../stores/cashcut-store.js'
import { useTenantId } from '../hooks/useTenantId.js'
import { useAuthStore } from '../stores/auth-store.js'
import { requestManagerPin } from '../features/security/pin-gate-store.js'
import {
  formatCents, totalFromDenominations, emptyDenominationCounts, MXN_DENOMINATIONS, type DenominationCounts,
} from '../lib/money.js'
import { openRetiroCompra, listPettyCashPurchases, PETTY_CASH_PURCHASE_REASON } from '../lib/clients/petty-cash.js'
import { listOrders, type BackendOrderDto } from '../lib/clients/orders.js'
import { getReceiptOpts } from '../lib/print/receipt-opts.js'
import { printReceipt, type CanonicalReceipt, type TextLine, type ReceiptTotal } from '../lib/bridge/bridge-client.js'
import { getPrintingConfig, pushPrintHistory } from '../lib/bridge/printing-config-store.js'
import { trackRelayJob } from '../lib/bridge/relay-job-tracker.js'
import { gqlClient } from '../lib/gql.js'
import { PosReportPaymentMethodsQuery } from '../lib/queries/reports.queries.js'
import { getCashCutExpected, type CashCutExpected } from '../lib/clients/cashcuts.js'
import { HelpTip } from '../components/HelpTip.js'

const KIND_LABEL: Record<string, string> = {
  OPEN: 'Apertura',
  X_REPORT: 'Corte X',
  CLOSE: 'Cierre',
  Z_REPORT: 'Corte Z',
  CASH_DROP: 'Retiro',
  CASH_PICKUP: 'Ingreso',
}

const KIND_DESCRIPTION: Record<string, string> = {
  OPEN: 'Fondo inicial declarado al abrir caja',
  X_REPORT: 'Snapshot del turno (no cierra)',
  CLOSE: 'Cierre del turno',
  Z_REPORT: 'Cierre del turno con resumen del día',
  CASH_DROP: 'Retiro de efectivo (al safe)',
  CASH_PICKUP: 'Ingreso de efectivo (cambio agregado)',
}

// ── Presets de fondo (sumas razonables con denominaciones realistas) ─────────
// Cada preset = mapa { valueCents → count } que se aplica al stepper.
const FONDO_PRESETS: readonly { label: string; totalCents: number; denoms: DenominationCounts }[] = [
  // $500: 5×$100
  { label: '$500',   totalCents:  50_000, denoms: { 10_000: 5 } },
  // $1,000: 1×$500 + 5×$100
  { label: '$1,000', totalCents: 100_000, denoms: { 50_000: 1, 10_000: 5 } },
  // $2,000: 2×$500 + 10×$100
  { label: '$2,000', totalCents: 200_000, denoms: { 50_000: 2, 10_000: 10 } },
  // $3,000: 2×$1,000 + 2×$500
  { label: '$3,000', totalCents: 300_000, denoms: { 100_000: 2, 50_000: 2 } },
  // $5,000: 4×$1,000 + 2×$500
  { label: '$5,000', totalCents: 500_000, denoms: { 100_000: 4, 50_000: 2 } },
]

function applyPreset(preset: DenominationCounts): DenominationCounts {
  const out = emptyDenominationCounts()
  for (const [k, v] of Object.entries(preset)) out[Number(k)] = v
  return out
}

function denomsToList(counts: DenominationCounts): { denomination: number; count: number }[] {
  return Object.entries(counts)
    .map(([v, c]) => ({ denomination: Number(v), count: c }))
    .filter((d) => d.count > 0)
}

// ── Petty-cash: motivo del retiro (FASE 1 del ciclo 2-fases) ─────────────────
// Al hacer un CASH_DROP el cajero tipifica POR QUÉ saca el efectivo. "Compra a
// proveedor" (PETTY_CASH_PURCHASE) abre un PettyCashPurchase (dhk8-gastos) en
// estado OPEN — sin líneas todavía. La conciliación (líneas + soportes + cambio)
// pasa a una vista de detalle aparte (PettyCashDetailPage, /cashout/retiros/:id),
// posiblemente en otro turno/cajero. Los demás motivos son sólo un `reason` en
// el corte, exactamente como antes.
const PETTY_CASH_PURCHASE = PETTY_CASH_PURCHASE_REASON

const DROP_REASONS: readonly { value: string; label: string }[] = [
  { value: PETTY_CASH_PURCHASE, label: 'Compra a proveedor' },
  { value: 'ARQUEO', label: 'Arqueo' },
  { value: 'EXCESO_EFECTIVO', label: 'Exceso de efectivo' },
  { value: 'SECURITY', label: 'Seguridad/guardar' },
  { value: 'OTHER', label: 'Otro' },
]

function uuid(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  return c?.randomUUID ? c.randomUUID() : `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

// ── Pagos por método (efectivo/tarjeta) — reusa reportPaymentMethods (Ola 2) ──
const METHOD_LABEL: Record<string, string> = {
  CASH: 'Efectivo', CARD_DEBIT: 'Tarjeta débito', CARD_CREDIT: 'Tarjeta crédito',
  TRANSFER: 'Transferencia', MEAL_VOUCHER: 'Vale', OTHER: 'Otro',
}

interface MethodRow { method: string; count: number; amountCents: number }

/** Fecha de hoy en GDL (UTC−6, sin DST). La tablet del café ya está en hora GDL,
 *  y reportPaymentMethods agrupa por día-de-negocio GDL del lado del servidor. */
function gdlTodayYmd(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Ventas del día por método (efectivo/tarjeta/…) para el Corte X/Z. */
function useDaySalesByMethod(tenantId: string | null, branchId: string | null) {
  const [rows, setRows] = useState<MethodRow[]>([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    if (!tenantId) { setLoading(false); return }
    let alive = true
    setLoading(true)
    const ymd = gdlTodayYmd()
    gqlClient
      .query(PosReportPaymentMethodsQuery, { from: ymd, to: ymd, ...(branchId ? { branchId } : {}) }, { requestPolicy: 'network-only' })
      .toPromise()
      .then((r) => { if (alive) setRows((r.data?.reportPaymentMethods ?? []) as MethodRow[]) })
      .catch(() => { if (alive) setRows([]) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [tenantId, branchId])
  const cashCents = rows.filter((r) => r.method === 'CASH').reduce((s, r) => s + r.amountCents, 0)
  const cardCents = rows.filter((r) => r.method === 'CARD_DEBIT' || r.method === 'CARD_CREDIT').reduce((s, r) => s + r.amountCents, 0)
  return { rows, cashCents, cardCents, loading }
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function CashoutPage() {
  const cuts = useCashcutStore((s) => s.cuts)
  const shiftOpen = useCashcutStore((s) => s.shiftOpen)
  const openCut = useCashcutStore((s) => s.openCut)
  const loading = useCashcutStore((s) => s.loading)
  const error = useCashcutStore((s) => s.error)
  const fetchCuts = useCashcutStore((s) => s.fetchCuts)
  // Re-fetch cuando cambia el tenant activo (env switch Test ↔ Live).
  const tenantId = useTenantId()

  useEffect(() => { void fetchCuts() }, [fetchCuts, tenantId])

  // Saldo de caja en curso (fondo + pickups − drops, calculado de la historia
  // del turno actual). Útil como "efectivo esperado" en X/Z.
  const runningCashCents = useMemo(() => runningCashFromCuts(cuts, openCut?.id ?? null), [cuts, openCut])

  return (
    <div className="dhk8-cashcut-page">
      <header className="dhk8-cashcut-page__head">
        <div>
          <h1 className="dhk8-cashcut-page__title">Caja</h1>
          <p className="dhk8-cashcut-page__sub">Apertura · movimientos · cortes X / Z</p>
        </div>
        {shiftOpen && openCut && (
          <span className="dhk8-cashcut-badge dhk8-cashcut-badge--open">
            Caja abierta · fondo {formatCents(openCut.countedCents)}
            {runningCashCents !== openCut.countedCents && (
              <span className="dhk8-cashcut-badge__delta"> → estimado {formatCents(runningCashCents)}</span>
            )}
          </span>
        )}
      </header>

      {error && <div className="dhk8-cashcut-error">{error}</div>}

      {shiftOpen
        ? <OpenShiftSection openFondo={openCut?.countedCents ?? 0} runningCash={runningCashCents} />
        : <OpenDrawerForm />}

      <CutHistory rows={cuts} loading={loading} />

      <style>{styles}</style>
    </div>
  )
}

// ── Apertura ────────────────────────────────────────────────────────────────

function OpenDrawerForm() {
  const recordCut = useCashcutStore((s) => s.recordCut)
  const [denoms, setDenoms] = useState<DenominationCounts>(() => emptyDenominationCounts())
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const [autoPrint, setAutoPrint] = useState(true)
  const fondoCents = totalFromDenominations(denoms)
  const tenantId = useTenantId() ?? ''

  async function handleOpen() {
    if (fondoCents <= 0) { setLocalError('Declara al menos un billete o moneda'); return }
    setBusy(true); setLocalError(null)
    try {
      const created = await recordCut({
        kind: 'OPEN',
        denominations: denomsToList(denoms),
        expectedCents: 0,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      })
      setDenoms(emptyDenominationCounts()); setNotes('')
      if (autoPrint) void printCashcut(created, tenantId)
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'No se pudo abrir caja')
    } finally { setBusy(false) }
  }

  return (
    <div className="dhk8-cashcut-card">
      <div className="dhk8-cashcut-card__head">
        <h2>Abrir caja <HelpTip text="El fondo es el efectivo con el que arranca la caja (para dar cambio). Cuéntalo por denominación; será la base del efectivo esperado al cerrar." /></h2>
        <p>Declara el fondo inicial desglosado por denominación.</p>
      </div>

      <div className="dhk8-presets">
        <span className="dhk8-presets__label">Atajos</span>
        {FONDO_PRESETS.map((p) => (
          <button
            type="button"
            key={p.totalCents}
            className={`dhk8-presets__chip${fondoCents === p.totalCents ? ' dhk8-presets__chip--active' : ''}`}
            onClick={() => setDenoms(applyPreset(p.denoms))}
            disabled={busy}
          >{p.label}</button>
        ))}
        <button type="button" className="dhk8-presets__chip dhk8-presets__chip--ghost" onClick={() => setDenoms(emptyDenominationCounts())} disabled={busy}>
          Limpiar
        </button>
      </div>

      <DenominationsGrid value={denoms} onChange={setDenoms} title="Fondo inicial" disabled={busy} />

      <div className="dhk8-cashcut-field">
        <label>Notas (opcional)</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Turno matutino, cambio pre-autorizado…" rows={2} />
      </div>

      {localError && <div className="dhk8-cashcut-error">{localError}</div>}

      <div className="dhk8-cashcut-footer">
        <label className="dhk8-cashcut-toggle">
          <input type="checkbox" checked={autoPrint} onChange={(e) => setAutoPrint(e.target.checked)} disabled={busy} />
          <Printer size={14} /> Imprimir comprobante
        </label>
        <button type="button" className="dhk8-cashcut-btn dhk8-cashcut-btn--primary dhk8-cashcut-btn--big" disabled={busy || fondoCents <= 0} onClick={handleOpen}>
          {busy ? 'Abriendo…' : `Abrir caja con ${formatCents(fondoCents)}`}
        </button>
      </div>
    </div>
  )
}

// ── Caja abierta ────────────────────────────────────────────────────────────

function OpenShiftSection({ openFondo, runningCash }: { openFondo: number; runningCash: number }) {
  type DialogKind = Extract<CashCutKind, 'X_REPORT' | 'Z_REPORT' | 'CASH_DROP' | 'CASH_PICKUP'>
  const [dialog, setDialog] = useState<null | { kind: DialogKind }>(null)
  const recordCut = useCashcutStore((s) => s.recordCut)
  const [cancelBusy, setCancelBusy] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const branchId = useAuthStore((s) => s.user?.branchId ?? null)

  // Toast local para la FASE 1 del ciclo petty-cash ("Retiro abierto, pendiente
  // de conciliar") — el diálogo se cierra de inmediato tras abrir el retiro, así
  // que el mensaje de éxito vive un instante aquí (patrón de TicketsPage).
  const [toast, setToast] = useState<string | null>(null)
  function flashToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3200)
  }

  // Retiros pendientes de conciliar (petty-cash) — pintan el acceso a la lista
  // y, al cerrar Z, avisan (sin bloquear) que quedaron compras sin itemizar.
  const [pendingCount, setPendingCount] = useState<number | null>(null)
  useEffect(() => {
    let alive = true
    listPettyCashPurchases({ status: 'OPEN', ...(branchId ? { branchId } : {}), limit: 200 })
      .then((items) => { if (alive) setPendingCount(items.length) })
      .catch(() => { if (alive) setPendingCount(null) })
    return () => { alive = false }
  }, [branchId, dialog])

  // Gate de gerente: retiros, ingresos y cortes (X/Z) SOLO con PIN de manager.
  // Se pide al INICIAR la acción; si aprueba, abre el diálogo para ejecutarla.
  const GATE: Record<DialogKind, 'cash.drop' | 'cash.pickup' | 'cash.xreport' | 'cash.close'> = {
    CASH_DROP: 'cash.drop', CASH_PICKUP: 'cash.pickup', X_REPORT: 'cash.xreport', Z_REPORT: 'cash.close',
  }
  async function openGated(kind: DialogKind) {
    const { approved } = await requestManagerPin(GATE[kind])
    if (!approved) return
    setDialog({ kind })
  }

  // Lane #172 — Anular turno: cuando hay que cerrar el turno sin proceso normal
  // de Z (ej: cambio de gerente forzado, sistema atorado). Pide manager-PIN +
  // razón obligatoria, registra un Z_REPORT cuadrado contra el fondo declarado
  // y anota el motivo en el corte para auditoría. NO cuadra el faltante real
  // — sólo cierra el shift para que se pueda abrir uno nuevo.
  async function handleCancelShift() {
    const result = await requestManagerPin('cash.cancelShift', {
      requireReason: true,
      contextLabel: `Fondo ${formatCents(openFondo)} · efectivo estimado ${formatCents(runningCash)}`,
    })
    if (!result.approved) return
    const reason = (result.reason ?? '').trim() || 'Turno anulado por gerente'
    setCancelBusy(true); setCancelError(null)
    try {
      await recordCut({
        kind: 'Z_REPORT',
        countedCents: openFondo,
        expectedCents: openFondo,
        notes: `[ANULADO] ${reason}`,
      })
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : 'No se pudo anular el turno')
    } finally {
      setCancelBusy(false)
    }
  }

  return (
    <div className="dhk8-cashcut-card">
      <div className="dhk8-cashcut-card__head">
        <h2>Turno en curso <HelpTip text="Mientras la caja está abierta puedes registrar retiros, ingresos y cortes. Todas estas operaciones requieren PIN de gerente." /></h2>
        <p>Fondo declarado {formatCents(openFondo)} · efectivo estimado {formatCents(runningCash)}.</p>
      </div>
      <div className="dhk8-cashcut-actions">
        <button type="button" className="dhk8-cashcut-btn dhk8-cashcut-btn--big" onClick={() => void openGated('CASH_DROP')}>
          <ArrowUpFromLine size={18} /> Retiro
          <HelpTip text="Sacar efectivo de la caja durante el turno (depósito al safe, pago a proveedor…). Resta del efectivo esperado. Requiere PIN de gerente." />
        </button>
        <button type="button" className="dhk8-cashcut-btn dhk8-cashcut-btn--big" onClick={() => void openGated('CASH_PICKUP')}>
          <ArrowDownToLine size={18} /> Ingreso
          <HelpTip text="Meter efectivo a la caja durante el turno (cambio agregado, devolución de fondo…). Suma al efectivo esperado. Requiere PIN de gerente." />
        </button>
        <button type="button" className="dhk8-cashcut-btn dhk8-cashcut-btn--big" onClick={() => void openGated('X_REPORT')}>
          <ClipboardCheck size={18} /> Corte X
          <HelpTip text="Lectura del turno SIN cerrarlo: compara el efectivo contado contra el esperado. Útil para arqueos a media jornada. Requiere PIN de gerente." />
        </button>
        <button type="button" className="dhk8-cashcut-btn dhk8-cashcut-btn--primary dhk8-cashcut-btn--big" onClick={() => void openGated('Z_REPORT')}>
          <Wallet size={18} /> Cerrar caja (Z)
          <HelpTip text="Cierre del turno: cuenta el efectivo final, registra la diferencia contra lo esperado y deja la caja lista para el siguiente turno. Requiere PIN de gerente." />
        </button>
        {/* Lane #172 — escape hatch para cerrar turno sin Z normal. */}
        <button type="button" className="dhk8-cashcut-btn dhk8-cashcut-btn--big dhk8-cashcut-btn--danger" onClick={() => void handleCancelShift()} disabled={cancelBusy}>
          <Ban size={18} /> {cancelBusy ? 'Anulando…' : 'Anular turno'}
          <HelpTip text="Cerrar el turno a la fuerza sin el proceso normal de Z (sistema atorado, cambio de gerente). NO cuadra el faltante real, solo libera la caja. Requiere PIN de gerente + motivo." />
        </button>
        {/* Ciclo petty-cash 2-fases — acceso visible a los retiros abiertos por
            "Compra a proveedor" que aún no se itemizan (líneas + soportes + cambio). */}
        <Link to="/cashout/retiros" className="dhk8-cashcut-btn dhk8-cashcut-btn--big">
          <ClipboardList size={18} /> Retiros pendientes
          {Boolean(pendingCount) && <span className="dhk8-cashcut-pill">{pendingCount}</span>}
        </Link>
      </div>
      {cancelError && <p className="dhk8-cashcut-card__err" role="alert">{cancelError}</p>}
      {dialog && (
        <CutDialog
          kind={dialog.kind}
          expectedDefault={runningCash}
          openFondo={openFondo}
          onClose={() => setDialog(null)}
          onPurchaseOpened={flashToast}
          pendingPettyCashCount={dialog.kind === 'Z_REPORT' ? pendingCount : null}
        />
      )}
      {toast && <div className="dhk8-cashcut-toast" role="status">{toast}</div>}
    </div>
  )
}

// ── Diálogo de corte / movimiento ───────────────────────────────────────────

function CutDialog({ kind, expectedDefault, openFondo, onClose, onPurchaseOpened, pendingPettyCashCount }:
  {
    kind: 'X_REPORT' | 'Z_REPORT' | 'CASH_DROP' | 'CASH_PICKUP'
    expectedDefault: number
    openFondo: number
    onClose: () => void
    /** Sólo se invoca para CASH_DROP con reason=Compra a proveedor, tras abrir el
     *  PettyCashPurchase (FASE 1 del ciclo 2-fases) — muestra el toast/mensaje de
     *  éxito en el padre (OpenShiftSection ya cerró este diálogo). */
    onPurchaseOpened?: (msg: string) => void
    /** Sólo para Z_REPORT: # de retiros petty-cash abiertos sin conciliar — se
     *  muestra como AVISO (no bloquea el cierre, decisión de producto). */
    pendingPettyCashCount?: number | null
  }) {
  const recordCut = useCashcutStore((s) => s.recordCut)
  const fetchCuts = useCashcutStore((s) => s.fetchCuts)
  const shiftId = useCashcutStore((s) => s.shiftId)
  const [denoms, setDenoms] = useState<DenominationCounts>(() => emptyDenominationCounts())
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const [autoPrint, setAutoPrint] = useState(true)
  const tenantId = useTenantId() ?? ''
  const branchId = useAuthStore((s) => s.user?.branchId ?? null)
  const cashierId = useAuthStore((s) => s.user?.id ?? null)
  // Ventas por método del día — SOLO informativo en el card de métodos (tarjeta/
  // transferencia). El efectivo esperado ya NO se deriva de aquí (era day-scoped).
  const dayPay = useDaySalesByMethod(tenantId || null, branchId)

  const isClose = kind === 'Z_REPORT'
  const isReport = kind === 'X_REPORT' || kind === 'Z_REPORT'
  const isMovement = kind === 'CASH_DROP' || kind === 'CASH_PICKUP'
  const isDrop = kind === 'CASH_DROP'

  // Lane petty-cash (FASE 1 del ciclo 2-fases): motivo del retiro (sólo
  // CASH_DROP). "Compra a proveedor" abre un PettyCashPurchase OPEN en
  // dhk8-gastos — la itemización (líneas + soportes + cambio) se hace DESPUÉS,
  // en la vista de detalle (/cashout/retiros/:id, ver PettyCashDetailPage).
  const [reason, setReason] = useState<string>('SECURITY')
  const isPurchase = isDrop && reason === PETTY_CASH_PURCHASE

  // Idempotencia estable entre reintentos: el purchaseRef liga el CASH_DROP con
  // el PettyCashPurchase que se abre a continuación (openRetiroCompra).
  const [purchaseRef] = useState<string>(() => uuid())
  const [createdDropId, setCreatedDropId] = useState<string | null>(null)

  // Efectivo ESPERADO autoritativo: lo deriva el SERVIDOR (fondo + ventas efectivo
  // del turno + ingresos − retiros). El cliente ya NO lo calcula — eso causaba el
  // "remanente" falso (usaba ventas del día y no restaba retiros). El backend lo
  // re-deriva también al guardar, así que el corte siempre cuadra contra ventas reales.
  const [srvExpected, setSrvExpected] = useState<CashCutExpected | null>(null)
  const [expectedLoading, setExpectedLoading] = useState(false)
  useEffect(() => {
    if (!isReport || !shiftId) return
    let alive = true
    setExpectedLoading(true)
    getCashCutExpected(shiftId, branchId ?? undefined)
      .then((e) => { if (alive) setSrvExpected(e) })
      .catch(() => { /* fallback al cálculo local (expectedDefault) si el server falla */ })
      .finally(() => { if (alive) setExpectedLoading(false) })
    return () => { alive = false }
  }, [isReport, shiftId, branchId])

  const countedCents = totalFromDenominations(denoms)
  // Esperado server-side; fallback al local (fondo + ingresos − retiros) si aún no carga.
  const expectedCents = srvExpected?.expectedCents ?? expectedDefault
  const varianceCents = countedCents - expectedCents
  const suggestedWithdrawCents = Math.max(0, expectedCents - openFondo)

  // Retiro a abrir como compra (FASE 1): lo contado en denominaciones.
  const retiradoCents = countedCents

  // ── Orquestación FASE 1 (reason = Compra a proveedor) ─────────────────────
  // (a) crea el CASH_DROP (reason=PETTY_CASH_PURCHASE, purchaseRef); (b) abre el
  // PettyCashPurchase (OPEN, sin líneas) vía openRetiroCompra, mandando branchId +
  // cashierId + shiftId — la conciliación (líneas/soportes/cambio) queda pendiente
  // para la vista de detalle. Ambos pasos son idempotentes por purchaseRef si el
  // submit se reintenta (createdDropId evita duplicar el CASH_DROP).
  async function handlePurchaseSubmit(): Promise<void> {
    if (retiradoCents <= 0) { setLocalError('Cuenta el efectivo que sacas del cajón'); return }
    setBusy(true); setLocalError(null)
    try {
      let dropId = createdDropId
      if (!dropId) {
        const drop = await recordCut({
          kind: 'CASH_DROP', reason: PETTY_CASH_PURCHASE, purchaseRef,
          denominations: denomsToList(denoms), countedCents: retiradoCents, expectedCents: retiradoCents,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        })
        dropId = drop.id
        setCreatedDropId(dropId)
        if (autoPrint) void printCashcut(drop, tenantId)
      }
      await openRetiroCompra({
        withdrawnCents: retiradoCents,
        idempotencyKey: purchaseRef,
        cashDropId: dropId,
        ...(branchId ? { branchId } : {}),
        ...(cashierId ? { cashierId } : {}),
        ...(shiftId ? { shiftId } : {}),
      })
      void fetchCuts()
      onPurchaseOpened?.('Retiro abierto, pendiente de conciliar.')
      onClose()
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'No se pudo abrir el retiro de compra')
    } finally { setBusy(false) }
  }

  async function handleSubmit() {
    if (isPurchase) { await handlePurchaseSubmit(); return }
    if (countedCents <= 0) { setLocalError('Cuenta al menos un billete o moneda'); return }
    if (isClose && varianceCents !== 0 && !notes.trim()) {
      setLocalError('La diferencia requiere una nota de justificación'); return
    }
    // El PIN de gerente ya se pidió al INICIAR la operación (gate cash.* en
    // OpenShiftSection.openGated). Aquí ya no se re-pide: la nota obligatoria por
    // diferencia (arriba) cubre la justificación de un faltante/sobrante grande.
    setBusy(true); setLocalError(null)
    try {
      const created = await recordCut({
        kind,
        denominations: denomsToList(denoms),
        countedCents,
        // Movimientos: expected = countedCents (no hay variance esperada)
        expectedCents: isMovement ? countedCents : expectedCents,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        // Lane petty-cash: tipifica el retiro con el motivo elegido.
        ...(isDrop ? { reason } : {}),
      })
      if (autoPrint) void printCashcut(created, tenantId)
      onClose()
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'No se pudo registrar el corte')
    } finally { setBusy(false) }
  }

  const submitDisabled = busy || (isPurchase
    ? retiradoCents <= 0
    : countedCents <= 0 || (isClose && varianceCents !== 0 && !notes.trim()))
  const submitLabel = busy
    ? 'Abriendo…'
    : isPurchase ? `Abrir retiro ${formatCents(retiradoCents)}`
    : kind === 'X_REPORT' ? 'Registrar corte X'
    : kind === 'Z_REPORT' ? 'Confirmar cierre'
    : kind === 'CASH_DROP' ? `Retirar ${formatCents(countedCents)}`
    : `Agregar ${formatCents(countedCents)}`

  return (
    <div className="dhk8-dialog-overlay" onClick={() => !busy && onClose()}>
      <div className="dhk8-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dhk8-dialog__head">
          <div>
            <div className="dhk8-dialog__title">{KIND_LABEL[kind]}</div>
            <div className="dhk8-dialog__sub">{KIND_DESCRIPTION[kind]}</div>
          </div>
          <button type="button" className="dhk8-dialog__close" aria-label="Cerrar" onClick={onClose} disabled={busy}>
            <X size={18} />
          </button>
        </div>

        {isDrop && (
          <div className="dhk8-cashcut-field">
            <label>Motivo del retiro <HelpTip
              text="Por qué sacas efectivo de la caja. «Compra a proveedor» abre el retiro como pendiente de conciliar (itemizas la compra y sus soportes después, en Retiros pendientes); los demás sólo etiquetan el retiro."
              defs={[
                { term: 'Compra a proveedor', desc: 'Sales a comprar algo con dinero de la caja — se concilia después en Retiros pendientes.' },
                { term: 'Arqueo', desc: 'Cuentas el efectivo para verificar que cuadre con lo esperado.' },
                { term: 'Exceso de efectivo', desc: 'Sacas dinero de más del cajón para no acumularlo.' },
                { term: 'Seguridad / guardar', desc: 'Retiras efectivo para llevarlo a la caja fuerte / depósito.' },
                { term: 'Otro', desc: 'Cualquier otro motivo; sólo queda etiquetado en el corte.' },
              ]}
            /></label>
            <select className="dhk8-petty-select" value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy}>
              {DROP_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        )}

        {isClose && Boolean(pendingPettyCashCount) && (
          <p className="dhk8-cashcut-warn" role="alert">
            Hay {pendingPettyCashCount} retiro{pendingPettyCashCount === 1 ? '' : 's'} sin conciliar
            (compra a proveedor pendiente de itemizar) — puedes cerrar la caja igual; concílialos
            después en <em>Retiros pendientes</em>.
          </p>
        )}
        {isClose && <ZSummary />}
        {isReport && <MethodBreakdown rows={dayPay.rows} loading={dayPay.loading} />}

        <DenominationsGrid
          value={denoms}
          onChange={setDenoms}
          title={isMovement ? `Efectivo a ${kind === 'CASH_DROP' ? 'retirar' : 'agregar'}` : 'Conteo de efectivo'}
          disabled={busy}
          expectedCents={isReport ? expectedCents : undefined}
        />

        {isPurchase && (
          <p className="dhk8-cashcut-hint">
            Este retiro queda <strong>pendiente de conciliar</strong>: la itemización (qué compraste,
            proveedor, soportes y cambio) se hace después en <em>Retiros pendientes</em> — puede
            hacerla otro cajero, en otro turno.
          </p>
        )}

        {isReport && (
          <div className="dhk8-cashcut-field">
            <label>Efectivo esperado por el sistema <HelpTip text="Lo que DEBERÍA haber en la caja según el sistema: fondo de apertura + ventas en efectivo del turno + ingresos − retiros. Lo calcula el sistema, no se edita a mano." /></label>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-lg)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {expectedLoading ? 'Calculando…' : formatCents(expectedCents)}
            </div>
            {srvExpected ? (
              <p className="dhk8-cashcut-hint">
                Fondo {formatCents(srvExpected.fondoCents)} + ventas en efectivo {formatCents(srvExpected.cashSalesCents)}
                {srvExpected.pickupsCents > 0 ? ` + ingresos ${formatCents(srvExpected.pickupsCents)}` : ''}
                {srvExpected.dropsCents > 0 ? ` − retiros ${formatCents(srvExpected.dropsCents)}` : ''}
                {' '}del turno (calculado por el sistema).
              </p>
            ) : (
              <p className="dhk8-cashcut-hint">= fondo + ventas en efectivo + ingresos − retiros del turno.</p>
            )}
            <p className="dhk8-cashcut-hint">Sugerido a retirar (deja el fondo de {formatCents(openFondo)} en caja): <strong>{formatCents(suggestedWithdrawCents)}</strong></p>
          </div>
        )}

        {isReport && (
          <div className="dhk8-cashcut-variance">
            Diferencia <HelpTip text="Efectivo contado − efectivo esperado. Positivo = sobrante (hay de más); negativo = faltante (hay de menos). Si no es cero, anota el motivo." />:{' '}
            <strong className={varianceCents === 0 ? '' : varianceCents > 0 ? 'dhk8-pos' : 'dhk8-neg'}>
              {varianceCents === 0
                ? '✓ cuadra'
                : `${varianceCents > 0 ? '+' : '−'}${formatCents(Math.abs(varianceCents))} (${varianceCents > 0 ? 'sobrante' : 'faltante'})`}
            </strong>
          </div>
        )}

        <div className="dhk8-cashcut-field">
          <label>{isDrop ? 'Notas (opcional)' : isMovement ? 'Motivo' : `Notas ${isClose && varianceCents !== 0 ? '(obligatorio si hay diferencia)' : '(opcional)'}`}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={isMovement
              ? (kind === 'CASH_DROP' ? 'Depósito al safe, pago a proveedor…' : 'Cambio agregado, devolución de fondo…')
              : 'Justificación de la diferencia, incidentes del turno…'}
            rows={3}
          />
        </div>

        {localError && <div className="dhk8-cashcut-error">{localError}</div>}

        <div className="dhk8-dialog__actions">
          <label className="dhk8-cashcut-toggle">
            <input type="checkbox" checked={autoPrint} onChange={(e) => setAutoPrint(e.target.checked)} disabled={busy} />
            <Printer size={14} /> Imprimir
          </label>
          <div className="dhk8-dialog__actions-right">
            <button type="button" onClick={onClose} disabled={busy}>Cancelar</button>
            <button
              type="button"
              className="dhk8-dialog__btn--primary"
              disabled={submitDisabled}
              onClick={handleSubmit}
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Z Summary (resumen del día) ─────────────────────────────────────────────

interface DaySummary {
  totalSalesCents: number
  totalTipsCents: number
  totalTaxCents: number
  ordersCount: number
  averageTicketCents: number
}

function ZSummary() {
  const tenantId = useTenantId()
  const branchId = useAuthStore((s) => s.user?.branchId ?? null)
  const [summary, setSummary] = useState<DaySummary | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!tenantId) return
    const ctrl = new AbortController()
    setLoading(true)
    const start = new Date(); start.setHours(0, 0, 0, 0)
    const end = new Date();   end.setHours(23, 59, 59, 999)
    listOrders({
      tenantId,
      ...(branchId ? { branchId } : {}),
      status: 'CLOSED',
      from: start.toISOString(),
      to: end.toISOString(),
      limit: 100,
    }, ctrl.signal)
      .then((r) => setSummary(aggregateDay(r.items)))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false))
    return () => ctrl.abort()
  }, [tenantId, branchId])

  return (
    <div className="dhk8-zsum">
      <div className="dhk8-zsum__title">Resumen del día</div>
      {loading ? (
        <div className="dhk8-zsum__loading">Cargando…</div>
      ) : !summary ? (
        <div className="dhk8-zsum__loading">No se pudo cargar el resumen.</div>
      ) : (
        <div className="dhk8-zsum__grid">
          <Stat label="Ventas totales"   value={formatCents(summary.totalSalesCents)} accent />
          <Stat label="IVA del día"      value={formatCents(summary.totalTaxCents)} />
          <Stat label="Propinas"         value={formatCents(summary.totalTipsCents)} />
          <Stat label="# Tickets"        value={String(summary.ordersCount)} />
          <Stat label="Ticket promedio"  value={formatCents(summary.averageTicketCents)} />
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`dhk8-zsum__stat${accent ? ' dhk8-zsum__stat--accent' : ''}`}>
      <span className="dhk8-zsum__stat-label">{label}</span>
      <span className="dhk8-zsum__stat-value">{value}</span>
    </div>
  )
}

function aggregateDay(orders: BackendOrderDto[]): DaySummary {
  let sales = 0, tips = 0, tax = 0
  for (const o of orders) {
    sales += o.totalCents
    tips += o.tipCents
    tax += o.taxCents
  }
  const count = orders.length
  return {
    totalSalesCents: sales,
    totalTipsCents: tips,
    totalTaxCents: tax,
    ordersCount: count,
    averageTicketCents: count > 0 ? Math.round(sales / count) : 0,
  }
}

// ── Desglose por método de pago (Corte X/Z) ─────────────────────────────────

function MethodBreakdown({ rows, loading }: { rows: MethodRow[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="dhk8-zsum">
        <div className="dhk8-zsum__title">Ingresos por método (hoy)</div>
        <div className="dhk8-zsum__loading">Cargando…</div>
      </div>
    )
  }
  if (rows.length === 0) return null
  // Efectivo primero (define el retiro); el resto por monto descendente.
  const sorted = [...rows].sort((a, b) => (a.method === 'CASH' ? -1 : b.method === 'CASH' ? 1 : b.amountCents - a.amountCents))
  return (
    <div className="dhk8-zsum">
      <div className="dhk8-zsum__title">Ingresos por método (hoy)</div>
      <div className="dhk8-paymethods">
        {sorted.map((r) => (
          <div key={r.method} className={`dhk8-paymethod${r.method === 'CASH' ? ' dhk8-paymethod--cash' : ''}`}>
            <span className="dhk8-paymethod__label">{METHOD_LABEL[r.method] ?? r.method}<em> ×{r.count}</em></span>
            <span className="dhk8-paymethod__amt">{formatCents(r.amountCents)}</span>
          </div>
        ))}
      </div>
      <div className="dhk8-paymethods__hint">Efectivo = queda en caja · Tarjeta/transferencia = al banco</div>
    </div>
  )
}

// ── History ─────────────────────────────────────────────────────────────────

function CutHistory({ rows, loading }: { rows: CashCutDto[]; loading: boolean }) {
  return (
    <div className="dhk8-cashcut-card">
      <div className="dhk8-cashcut-card__head"><h2>Historial de cortes</h2><p>Últimos registros (todos los turnos).</p></div>
      {loading && rows.length === 0 ? (
        <div className="dhk8-cashcut-history__empty">Cargando…</div>
      ) : rows.length === 0 ? (
        <div className="dhk8-cashcut-history__empty">Sin cortes registrados.</div>
      ) : (
        <table className="dhk8-cashcut-history">
          <thead>
            <tr><th>Fecha</th><th>Tipo</th><th>Esperado</th><th>Contado</th><th>Diferencia</th><th>Notas</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isMov = r.kind === 'CASH_DROP' || r.kind === 'CASH_PICKUP'
              return (
                <tr key={r.id}>
                  <td>{formatDate(r.createdAt)}</td>
                  <td>
                    <span className={`dhk8-cashcut-kind dhk8-cashcut-kind--${r.kind.toLowerCase()}`}>
                      {KIND_LABEL[r.kind] ?? r.kind}
                    </span>
                  </td>
                  <td>{isMov ? '—' : formatCents(r.expectedCents)}</td>
                  <td>{formatCents(r.countedCents)}</td>
                  <td className={r.varianceCents === 0 || isMov ? '' : 'dhk8-cashcut-history__diff'}>
                    {isMov ? (r.kind === 'CASH_DROP' ? `−${formatCents(r.countedCents)}` : `+${formatCents(r.countedCents)}`)
                          : r.varianceCents === 0 ? '✓' : `${r.varianceCents > 0 ? '+' : '−'}${formatCents(Math.abs(r.varianceCents))}`}
                  </td>
                  <td className="dhk8-cashcut-history__notes" title={r.notes ?? ''}>{r.notes ?? ''}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ── DenominationsGrid (2-col bills | coins, steppers) ───────────────────────

function DenominationsGrid({ value, onChange, title, disabled, expectedCents }: {
  value: DenominationCounts
  onChange: (v: DenominationCounts) => void
  title: string
  disabled?: boolean
  expectedCents?: number
}) {
  const total = totalFromDenominations(value)
  const diff = expectedCents !== undefined ? total - expectedCents : null
  const bills = MXN_DENOMINATIONS.filter((d) => d.kind === 'bill')
  const coins = MXN_DENOMINATIONS.filter((d) => d.kind === 'coin')

  const billsTotal = bills.reduce((s, d) => s + (value[d.valueCents] ?? 0) * d.valueCents, 0)
  const coinsTotal = coins.reduce((s, d) => s + (value[d.valueCents] ?? 0) * d.valueCents, 0)

  function setCount(valueCents: number, count: number) {
    onChange({ ...value, [valueCents]: Math.max(0, Math.min(999, count)) })
  }

  return (
    <div className="dhk8-denoms">
      <div className="dhk8-denoms__title">
        {title}
        <span className="dhk8-denoms__total">
          Total: {formatCents(total)}
          {diff !== null && diff !== 0 && (
            <span className={`dhk8-denoms__diff${diff < 0 ? ' dhk8-denoms__diff--neg' : ' dhk8-denoms__diff--pos'}`}>
              ({diff > 0 ? '+' : ''}{formatCents(diff)})
            </span>
          )}
        </span>
      </div>

      <div className="dhk8-denoms__columns">
        <DenomColumn title="Billetes" items={bills} value={value} onChange={setCount} subtotal={billsTotal} disabled={disabled} />
        <DenomColumn title="Monedas"  items={coins} value={value} onChange={setCount} subtotal={coinsTotal} disabled={disabled} />
      </div>
    </div>
  )
}

function DenomColumn({ title, items, value, onChange, subtotal, disabled }: {
  title: string
  items: readonly { valueCents: number; label: string; kind: 'bill' | 'coin' }[]
  value: DenominationCounts
  onChange: (valueCents: number, count: number) => void
  subtotal: number
  disabled?: boolean
}) {
  return (
    <div className="dhk8-denoms__col">
      <div className="dhk8-denoms__col-head">
        <span>{title}</span>
        <span className="dhk8-denoms__col-sub">{formatCents(subtotal)}</span>
      </div>
      <div className="dhk8-denoms__col-rows">
        {items.map((d) => (
          <DenomRow
            key={d.valueCents}
            label={d.label}
            count={value[d.valueCents] ?? 0}
            valueCents={d.valueCents}
            onChange={(n) => onChange(d.valueCents, n)}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  )
}

function DenomRow({ label, count, valueCents, onChange, disabled }: {
  label: string
  count: number
  valueCents: number
  onChange: (count: number) => void
  disabled?: boolean
}) {
  const subtotal = count * valueCents
  return (
    <div className="dhk8-denoms__row">
      <span className="dhk8-denoms__row-label">{label}</span>
      <div className="dhk8-denoms__stepper">
        <button type="button" className="dhk8-denoms__step" aria-label={`Disminuir ${label}`}
          disabled={disabled || count <= 0} onClick={() => onChange(count - 1)}>
          <Minus size={18} />
        </button>
        <span className="dhk8-denoms__step-val" aria-live="polite">{count}</span>
        <button type="button" className="dhk8-denoms__step" aria-label={`Aumentar ${label}`}
          disabled={disabled || count >= 999} onClick={() => onChange(count + 1)}>
          <Plus size={18} />
        </button>
      </div>
      <span className="dhk8-denoms__row-sub">{formatCents(subtotal)}</span>
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Calcula el efectivo esperado en caja en este momento:
 *    fondo (OPEN) + Σ pickups − Σ drops − Σ X/Z previos (reset al último cut)
 *  Aquí simplificamos: solo fondo + pickups − drops del turno actual. El X/Z
 *  no "vacía" la caja (siguen los billetes), solo deja constancia. */
function runningCashFromCuts(cuts: CashCutDto[], openCutId: string | null): number {
  if (!openCutId) return 0
  // Cortes del turno actual: desde OPEN hacia adelante (orden cronológico).
  const sorted = [...cuts].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  const openIdx = sorted.findIndex((c) => c.id === openCutId)
  if (openIdx < 0) return 0
  const shift = sorted.slice(openIdx)
  let cash = 0
  for (const c of shift) {
    if (c.kind === 'OPEN') cash = c.countedCents
    else if (c.kind === 'CASH_PICKUP') cash += c.countedCents
    else if (c.kind === 'CASH_DROP') cash -= c.countedCents
  }
  return Math.max(0, cash)
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(d)
}

/** Imprime un comprobante del corte vía bridge. Best-effort — si no hay
 *  bridge configurado, falla silencioso. La impresión NO bloquea el flujo. */
async function printCashcut(cut: CashCutDto, tenantId: string): Promise<void> {
  try {
    const cfg = getPrintingConfig()
    if (!cfg?.defaultPrinter) return
    const header = getReceiptOpts(tenantId)
    const receipt = buildCashcutReceipt(cut, header)
    const r = await printReceipt(receipt, { printer: cfg.defaultPrinter })
    // relay: jobId → sigue progreso (el tracker escribe el history real cuando
    // sale el papel). host: sin jobId → ya imprimió, history 'ok' aquí.
    if (r.jobId) trackRelayJob(r.jobId, 'Corte de caja', 'receipt')
    else pushPrintHistory('receipt:cashcut', 'ok')
  } catch (e) {
    pushPrintHistory('receipt:cashcut', 'error', e instanceof Error ? e.message : String(e))
  }
}

function buildCashcutReceipt(cut: CashCutDto, header: { businessName?: string; branchName?: string }): CanonicalReceipt {
  const headerLines: TextLine[] = []
  if (header.businessName) headerLines.push({ text: header.businessName, align: 'center', bold: true, size: 'double-h' })
  if (header.branchName)   headerLines.push({ text: header.branchName, align: 'center' })
  headerLines.push({ text: '' })
  headerLines.push({ text: KIND_LABEL[cut.kind] ?? cut.kind, align: 'center', bold: true })
  headerLines.push({ text: formatDate(cut.createdAt), align: 'center' })

  const totals: ReceiptTotal[] = []
  const isMov = cut.kind === 'CASH_DROP' || cut.kind === 'CASH_PICKUP'
  if (!isMov) {
    totals.push({ label: 'Esperado', amountCents: cut.expectedCents })
  }
  totals.push({ label: 'Contado',  amountCents: cut.countedCents, bold: true })
  if (!isMov && cut.varianceCents !== 0) {
    totals.push({ label: 'Diferencia', amountCents: cut.varianceCents, bold: true })
  }

  // Desglose de denominaciones como líneas en el footer
  const denomLines: TextLine[] = []
  if (cut.denominations && cut.denominations.length > 0) {
    denomLines.push({ text: '' })
    denomLines.push({ text: '— Desglose —', align: 'center' })
    for (const d of cut.denominations) {
      const label = `$${(d.denomination / 100).toFixed(d.denomination % 100 === 0 ? 0 : 2).padStart(5, ' ')}`
      denomLines.push({ text: `${label} x ${String(d.count).padStart(3, ' ')}   ${formatCents(d.denomination * d.count).padStart(10, ' ')}` })
    }
  }
  if (cut.notes) {
    denomLines.push({ text: '' })
    denomLines.push({ text: 'Notas:', bold: true })
    denomLines.push({ text: cut.notes })
  }
  denomLines.push({ text: '' })
  denomLines.push({ text: 'Conserve este comprobante', align: 'center' })

  return {
    width: 42,
    encoding: 'cp858',
    header: headerLines,
    totals,
    footer: denomLines,
    cut: 'partial',
  }
}

// ── Styles ──────────────────────────────────────────────────────────────────

const styles = `
  .dhk8-cashcut-page { display:flex; flex-direction:column; gap:var(--space-4); }
  .dhk8-cashcut-page__head { display:flex; justify-content:space-between; align-items:flex-start; gap:var(--space-3); flex-wrap:wrap; }
  .dhk8-cashcut-page__title { font-family:var(--font-display); font-size:var(--text-xl); margin:0; color:var(--text-primary); }
  .dhk8-cashcut-page__sub { margin:0; font-family:var(--font-mono); font-size:var(--text-xs); color:var(--text-tertiary); }
  .dhk8-cashcut-badge { padding:var(--space-1) var(--space-2); border:1px solid var(--separator-opaque); color:var(--text-secondary); font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.08em; text-transform:uppercase; border-radius:var(--radius-sm); }
  .dhk8-cashcut-badge--open { border-color:var(--tint); color:var(--tint); background:var(--tint-bg); }
  .dhk8-cashcut-badge__delta { color:var(--text-tertiary); }
  .dhk8-cashcut-error { padding:var(--space-2) var(--space-3); background:var(--status-error-bg); border:1px solid var(--status-error); color:var(--status-error); font-family:var(--font-mono); font-size:var(--text-xs); border-radius:var(--radius-sm); }

  .dhk8-cashcut-card { display:flex; flex-direction:column; gap:var(--space-4); padding:var(--space-4); border:1px solid var(--separator-opaque); background:var(--bg-surface); border-radius:var(--radius-md); }
  .dhk8-cashcut-card__head h2 { font-family:var(--font-display); font-size:var(--text-lg); margin:0 0 var(--space-1) 0; color:var(--text-primary); }
  .dhk8-cashcut-card__head p { margin:0; font-family:var(--font-mono); font-size:var(--text-xs); color:var(--text-tertiary); }

  .dhk8-cashcut-actions { display:flex; flex-wrap:wrap; gap:var(--space-2); }
  .dhk8-cashcut-btn { display:inline-flex; align-items:center; gap:var(--space-2); padding:var(--space-2) var(--space-4); border:1px solid var(--separator-opaque); background:transparent; color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-sm); cursor:pointer; border-radius:var(--radius-sm); transition:background 120ms ease; text-decoration:none; }
  .dhk8-cashcut-btn:hover:not(:disabled) { background:var(--bg-elevated); }
  .dhk8-cashcut-btn:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-cashcut-btn--primary { background:var(--tint); color:var(--bg-canvas); border-color:var(--tint); }
  .dhk8-cashcut-btn--primary:hover:not(:disabled) { background:var(--tint); filter:brightness(1.1); }
  .dhk8-cashcut-btn--big { min-height:48px; padding:var(--space-3) var(--space-5); font-size:var(--text-base); }
  /* Lane #172 — variante danger para "Anular turno". */
  .dhk8-cashcut-btn--danger { border-color:var(--pos-danger,#E5484D); color:var(--pos-danger,#E5484D); }
  .dhk8-cashcut-btn--danger:hover:not(:disabled) { background:color-mix(in oklch, var(--pos-danger,#E5484D) 12%, transparent); }
  .dhk8-cashcut-card__err { margin:var(--space-2) 0 0 0; padding:var(--space-2) var(--space-3); color:var(--pos-danger,#E5484D); background:color-mix(in oklch, var(--pos-danger,#E5484D) 14%, transparent); border:1px solid color-mix(in oklch, var(--pos-danger,#E5484D) 40%, transparent); border-radius:var(--radius-sm); font-family:var(--font-mono); font-size:var(--text-xs); }

  /* Ciclo petty-cash 2-fases — badge de pendientes, toast de éxito, aviso al cerrar Z. */
  .dhk8-cashcut-pill { display:inline-flex; align-items:center; justify-content:center; min-width:20px; height:20px; padding:0 6px; border-radius:var(--radius-pill); background:var(--status-warning); color:var(--bg-canvas); font-family:var(--font-mono); font-size:var(--text-xxs); font-weight:700; }
  .dhk8-cashcut-toast { position:fixed; left:50%; bottom:24px; transform:translateX(-50%); z-index:120; max-width:90vw; padding:var(--space-3) var(--space-4); border-radius:var(--radius-lg); background:var(--tint); color:var(--bg-canvas); font-family:var(--font-mono); font-size:var(--text-sm); font-weight:600; box-shadow:var(--shadow-lg); }
  .dhk8-cashcut-warn { margin:0; padding:var(--space-2) var(--space-3); background:var(--status-warning-bg,rgba(241,179,59,.14)); border:1px solid var(--status-warning,#B8780B); color:var(--status-warning,#B8780B); font-family:var(--font-mono); font-size:var(--text-xs); border-radius:var(--radius-sm); }
  .dhk8-cashcut-warn em { font-style:normal; font-weight:600; }

  .dhk8-cashcut-field { display:flex; flex-direction:column; gap:var(--space-1); }
  .dhk8-cashcut-field label { font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.08em; text-transform:uppercase; color:var(--text-tertiary); }
  .dhk8-cashcut-field input,.dhk8-cashcut-field textarea { border:1px solid var(--separator-opaque); padding:var(--space-2) var(--space-3); background:var(--bg-canvas); color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-sm); outline:none; width:100%; border-radius:var(--radius-sm); }
  .dhk8-cashcut-hint { margin:0; font-family:var(--font-mono); font-size:var(--text-xxs); color:var(--text-tertiary); }

  .dhk8-cashcut-variance { font-family:var(--font-mono); font-size:var(--text-sm); color:var(--text-secondary); padding:var(--space-2) var(--space-3); border:1px dashed var(--separator-opaque); border-radius:var(--radius-sm); }
  .dhk8-cashcut-variance .dhk8-pos { color:var(--tint); } .dhk8-cashcut-variance .dhk8-neg { color:var(--status-error); }

  .dhk8-cashcut-footer { display:flex; justify-content:space-between; align-items:center; gap:var(--space-3); flex-wrap:wrap; }
  .dhk8-cashcut-toggle { display:inline-flex; align-items:center; gap:var(--space-2); font-family:var(--font-mono); font-size:var(--text-xs); color:var(--text-secondary); cursor:pointer; user-select:none; }
  .dhk8-cashcut-toggle input { width:18px; height:18px; cursor:pointer; }

  .dhk8-presets { display:flex; flex-wrap:wrap; gap:var(--space-2); align-items:center; }
  .dhk8-presets__label { font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.08em; text-transform:uppercase; color:var(--text-tertiary); }
  .dhk8-presets__chip { min-height:36px; padding:var(--space-1) var(--space-3); border:1px solid var(--separator-opaque); background:var(--bg-canvas); color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-xs); cursor:pointer; border-radius:var(--radius-pill); transition:background 120ms ease; }
  .dhk8-presets__chip:hover:not(:disabled) { background:var(--bg-elevated); }
  .dhk8-presets__chip:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-presets__chip--active { background:var(--tint); color:var(--bg-canvas); border-color:var(--tint); }
  .dhk8-presets__chip--ghost { color:var(--text-tertiary); }

  .dhk8-denoms { display:flex; flex-direction:column; gap:var(--space-3); }
  .dhk8-denoms__title { display:flex; justify-content:space-between; align-items:baseline; font-family:var(--font-mono); font-size:var(--text-xs); color:var(--text-tertiary); letter-spacing:.06em; text-transform:uppercase; }
  .dhk8-denoms__total { font-variant-numeric:tabular-nums; color:var(--text-primary); font-size:var(--text-sm); }
  .dhk8-denoms__diff { font-size:var(--text-xxs); margin-left:var(--space-1); }
  .dhk8-denoms__diff--neg { color:var(--status-error); } .dhk8-denoms__diff--pos { color:var(--tint); }

  .dhk8-denoms__columns { display:grid; grid-template-columns:1fr 1fr; gap:var(--space-4); }
  @media (max-width:720px) { .dhk8-denoms__columns { grid-template-columns:1fr; } }
  .dhk8-denoms__col { display:flex; flex-direction:column; gap:var(--space-2); padding:var(--space-3); border:1px solid var(--separator); border-radius:var(--radius-sm); background:var(--bg-canvas); }
  .dhk8-denoms__col-head { display:flex; justify-content:space-between; align-items:baseline; font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.08em; text-transform:uppercase; color:var(--text-tertiary); border-bottom:1px solid var(--separator); padding-bottom:var(--space-2); }
  .dhk8-denoms__col-sub { font-variant-numeric:tabular-nums; color:var(--text-secondary); font-size:var(--text-xs); }
  .dhk8-denoms__col-rows { display:flex; flex-direction:column; gap:var(--space-1); }

  .dhk8-denoms__row { display:grid; grid-template-columns:64px 1fr 90px; align-items:center; gap:var(--space-2); padding:var(--space-1) 0; }
  .dhk8-denoms__row-label { font-family:var(--font-mono); font-size:var(--text-sm); font-variant-numeric:tabular-nums; color:var(--text-primary); text-align:right; }
  .dhk8-denoms__row-sub { font-family:var(--font-mono); font-size:var(--text-xs); font-variant-numeric:tabular-nums; color:var(--text-tertiary); text-align:right; }

  .dhk8-denoms__stepper { display:flex; align-items:center; justify-content:center; gap:var(--space-2); }
  .dhk8-denoms__step { width:44px; height:44px; display:flex; align-items:center; justify-content:center; border:1px solid var(--separator-opaque); border-radius:var(--radius-md); background:var(--bg-surface); color:var(--text-primary); cursor:pointer; transition:background 120ms ease; touch-action:manipulation; }
  .dhk8-denoms__step:hover:not(:disabled) { background:var(--bg-elevated); }
  .dhk8-denoms__step:active:not(:disabled) { background:var(--tint-bg); }
  .dhk8-denoms__step:disabled { opacity:.3; cursor:not-allowed; }
  .dhk8-denoms__step-val { min-width:36px; text-align:center; font-family:var(--font-mono); font-size:var(--text-lg); font-weight:600; font-variant-numeric:tabular-nums; color:var(--text-primary); }

  .dhk8-cashcut-history { width:100%; border-collapse:collapse; font-family:var(--font-mono); font-size:var(--text-xs); font-variant-numeric:tabular-nums; }
  .dhk8-cashcut-history th,.dhk8-cashcut-history td { padding:var(--space-2); text-align:left; border-bottom:1px solid var(--separator); }
  .dhk8-cashcut-history th { color:var(--text-tertiary); font-size:var(--text-xxs); letter-spacing:.08em; text-transform:uppercase; }
  .dhk8-cashcut-history__diff { color:var(--status-warning); }
  .dhk8-cashcut-history__notes { color:var(--text-tertiary); max-width:240px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .dhk8-cashcut-history__empty { padding:var(--space-4); text-align:center; font-family:var(--font-mono); font-size:var(--text-xs); color:var(--text-tertiary); }

  .dhk8-cashcut-kind { display:inline-block; padding:2px 8px; border-radius:var(--radius-pill); font-size:10px; letter-spacing:.06em; text-transform:uppercase; border:1px solid var(--separator-opaque); }
  .dhk8-cashcut-kind--open { border-color:var(--tint); color:var(--tint); }
  .dhk8-cashcut-kind--z_report,.dhk8-cashcut-kind--close { background:var(--tint); color:var(--bg-canvas); border-color:var(--tint); }
  .dhk8-cashcut-kind--cash_drop { color:var(--status-warning); border-color:var(--status-warning); }
  .dhk8-cashcut-kind--cash_pickup { color:var(--status-success); border-color:var(--status-success); }

  .dhk8-dialog-overlay { position:fixed; inset:0; background:rgba(0,0,0,.5); display:flex; align-items:center; justify-content:center; z-index:100; padding:var(--space-3); }
  .dhk8-dialog { background:var(--bg-elevated); border:1px solid var(--separator-opaque); padding:var(--space-5); display:flex; flex-direction:column; gap:var(--space-4); min-width:380px; max-width:760px; width:100%; max-height:92vh; overflow-y:auto; border-radius:var(--radius-lg); box-shadow:var(--shadow-lg); }
  .dhk8-dialog__head { display:flex; justify-content:space-between; align-items:flex-start; gap:var(--space-3); }
  .dhk8-dialog__title { font-family:var(--font-display); font-size:var(--text-lg); color:var(--text-primary); }
  .dhk8-dialog__sub { font-family:var(--font-mono); font-size:var(--text-xs); color:var(--text-tertiary); margin-top:var(--space-1); }
  .dhk8-dialog__close { width:32px; height:32px; display:flex; align-items:center; justify-content:center; border:1px solid var(--separator-opaque); background:transparent; color:var(--text-secondary); cursor:pointer; border-radius:var(--radius-sm); }
  .dhk8-dialog__close:hover:not(:disabled) { background:var(--bg-canvas); }

  .dhk8-dialog__actions { display:flex; justify-content:space-between; align-items:center; gap:var(--space-2); flex-wrap:wrap; }
  .dhk8-dialog__actions-right { display:flex; gap:var(--space-2); margin-left:auto; }
  .dhk8-dialog__actions button { min-height:44px; padding:var(--space-2) var(--space-4); border:1px solid var(--separator-opaque); background:transparent; color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-sm); cursor:pointer; border-radius:var(--radius-sm); }
  .dhk8-dialog__actions button:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-dialog__btn--primary { background:var(--tint) !important; color:var(--bg-canvas) !important; border-color:var(--tint) !important; }

  .dhk8-zsum { padding:var(--space-3); background:var(--bg-canvas); border:1px solid var(--separator); border-radius:var(--radius-sm); display:flex; flex-direction:column; gap:var(--space-2); }
  .dhk8-zsum__title { font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.08em; text-transform:uppercase; color:var(--text-tertiary); }
  .dhk8-zsum__loading { font-family:var(--font-mono); font-size:var(--text-xs); color:var(--text-tertiary); }
  .dhk8-zsum__grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(140px, 1fr)); gap:var(--space-2); }
  .dhk8-zsum__stat { display:flex; flex-direction:column; gap:var(--space-1); padding:var(--space-2) var(--space-3); border:1px solid var(--separator); border-radius:var(--radius-sm); background:var(--bg-surface); }
  .dhk8-zsum__stat--accent { border-color:var(--tint); background:var(--tint-bg); }
  .dhk8-zsum__stat-label { font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.06em; text-transform:uppercase; color:var(--text-tertiary); }
  .dhk8-zsum__stat-value { font-family:var(--font-mono); font-size:var(--text-base); font-weight:600; font-variant-numeric:tabular-nums; color:var(--text-primary); }

  .dhk8-paymethods { display:flex; flex-direction:column; gap:var(--space-1); }
  .dhk8-paymethod { display:flex; justify-content:space-between; align-items:center; padding:var(--space-2) var(--space-3); border:1px solid var(--separator); border-radius:var(--radius-sm); background:var(--bg-surface); font-family:var(--font-mono); font-size:var(--text-sm); }
  .dhk8-paymethod--cash { border-color:var(--tint); background:var(--tint-bg); }
  .dhk8-paymethod__label { color:var(--text-secondary); }
  .dhk8-paymethod__label em { color:var(--text-tertiary); font-style:normal; font-size:var(--text-xxs); }
  .dhk8-paymethod__amt { font-variant-numeric:tabular-nums; font-weight:600; color:var(--text-primary); }
  .dhk8-paymethods__hint { font-family:var(--font-mono); font-size:var(--text-xxs); color:var(--text-tertiary); }

  /* ── Petty-cash purchase form ─────────────────────────────────────────── */
  .dhk8-petty { display:flex; flex-direction:column; gap:var(--space-4); padding:var(--space-3); border:1px solid var(--separator); border-radius:var(--radius-sm); background:var(--bg-canvas); }
  .dhk8-petty__section { display:flex; flex-direction:column; gap:var(--space-2); }
  .dhk8-petty__section-head { display:flex; justify-content:space-between; align-items:center; font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.08em; text-transform:uppercase; color:var(--text-tertiary); }
  .dhk8-petty__add { display:inline-flex; align-items:center; gap:var(--space-1); min-height:36px; padding:var(--space-1) var(--space-3); border:1px solid var(--separator-opaque); background:var(--bg-surface); color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-xs); cursor:pointer; border-radius:var(--radius-pill); }
  .dhk8-petty__add:hover:not(:disabled) { background:var(--bg-elevated); }
  .dhk8-petty__add:disabled { opacity:.5; cursor:not-allowed; }

  .dhk8-petty__lines { display:flex; flex-direction:column; gap:var(--space-3); }
  .dhk8-petty__line { display:flex; flex-direction:column; gap:var(--space-2); padding:var(--space-3); border:1px solid var(--separator); border-radius:var(--radius-sm); background:var(--bg-surface); }
  .dhk8-petty__line-label { border:1px solid var(--separator-opaque); padding:var(--space-2) var(--space-3); background:var(--bg-canvas); color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-sm); outline:none; width:100%; border-radius:var(--radius-sm); min-height:44px; }
  .dhk8-petty__line-tag { align-self:flex-start; font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.06em; text-transform:uppercase; color:var(--tint); border:1px solid var(--tint); padding:1px 6px; border-radius:var(--radius-pill); }
  .dhk8-petty__line-nums { display:grid; grid-template-columns:repeat(3, 1fr); gap:var(--space-2); }
  @media (max-width:520px) { .dhk8-petty__line-nums { grid-template-columns:1fr; } }
  .dhk8-petty__num { display:flex; flex-direction:column; gap:var(--space-1); }
  .dhk8-petty__num span { font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.06em; text-transform:uppercase; color:var(--text-tertiary); }
  .dhk8-petty__num input { border:1px solid var(--separator-opaque); padding:var(--space-2) var(--space-3); background:var(--bg-canvas); color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-sm); font-variant-numeric:tabular-nums; outline:none; width:100%; border-radius:var(--radius-sm); min-height:44px; text-align:right; }
  .dhk8-petty__line-foot { display:flex; justify-content:space-between; align-items:center; gap:var(--space-2); }
  .dhk8-petty__toggle { display:inline-flex; align-items:center; gap:var(--space-2); font-family:var(--font-mono); font-size:var(--text-xs); color:var(--text-secondary); cursor:pointer; user-select:none; min-height:44px; }
  .dhk8-petty__toggle input { width:20px; height:20px; cursor:pointer; }
  .dhk8-petty__rm { width:44px; height:44px; display:flex; align-items:center; justify-content:center; border:1px solid var(--separator-opaque); background:transparent; color:var(--text-tertiary); cursor:pointer; border-radius:var(--radius-sm); }
  .dhk8-petty__rm:hover:not(:disabled) { color:var(--pos-danger,#E5484D); border-color:var(--pos-danger,#E5484D); }
  .dhk8-petty__rm:disabled { opacity:.3; cursor:not-allowed; }

  .dhk8-petty-select { border:1px solid var(--separator-opaque); padding:var(--space-2) var(--space-3); background:var(--bg-canvas); color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-sm); outline:none; width:100%; border-radius:var(--radius-sm); min-height:44px; }

  .dhk8-petty__files { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:var(--space-1); }
  .dhk8-petty__file { display:flex; justify-content:space-between; align-items:center; gap:var(--space-2); padding:var(--space-1) var(--space-2); border:1px solid var(--separator); border-radius:var(--radius-sm); background:var(--bg-surface); font-family:var(--font-mono); font-size:var(--text-xs); color:var(--text-secondary); }
  .dhk8-petty__file--err { border-color:var(--pos-danger,#E5484D); color:var(--pos-danger,#E5484D); }
  .dhk8-petty__file-name { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .dhk8-petty__file button { width:32px; height:32px; display:flex; align-items:center; justify-content:center; border:none; background:transparent; color:inherit; cursor:pointer; }

  .dhk8-petty__money { display:flex; flex-direction:column; gap:var(--space-2); padding:var(--space-3); border:1px solid var(--separator); border-radius:var(--radius-sm); background:var(--bg-surface); }
  .dhk8-petty__money-row { display:flex; justify-content:space-between; align-items:center; gap:var(--space-3); font-family:var(--font-mono); font-size:var(--text-sm); color:var(--text-secondary); }
  .dhk8-petty__money-row strong { font-variant-numeric:tabular-nums; color:var(--text-primary); }
  .dhk8-petty__money-row--contrib { color:var(--status-warning); }
  .dhk8-petty__money-row--contrib strong { color:var(--status-warning); }
  .dhk8-petty__change { display:inline-flex; align-items:center; gap:var(--space-2); }
  .dhk8-petty__change input { width:120px; border:1px solid var(--separator-opaque); padding:var(--space-1) var(--space-2); background:var(--bg-canvas); color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-sm); font-variant-numeric:tabular-nums; text-align:right; outline:none; border-radius:var(--radius-sm); min-height:40px; }
  .dhk8-petty__reset { min-height:32px; padding:0 var(--space-2); border:1px solid var(--separator-opaque); background:transparent; color:var(--text-tertiary); font-family:var(--font-mono); font-size:var(--text-xxs); cursor:pointer; border-radius:var(--radius-pill); }
  .dhk8-petty__identity { padding:var(--space-2) var(--space-3); border-radius:var(--radius-sm); font-family:var(--font-mono); font-size:var(--text-xs); border:1px dashed var(--separator-opaque); }
  .dhk8-petty__identity--ok { color:var(--tint); border-color:var(--tint); }
  .dhk8-petty__identity--bad { color:var(--status-error); border-color:var(--status-error); background:var(--status-error-bg); }
`
